import chroma from 'chroma-js';

import { AAA_COTNRAST_RATIO, AA_CONTRAST_RATIO } from 'types/theme-packs';

/* 
	****IMPORTANT****
 
 This method does NOT guarantee a AAA spec.
 It will try to get as close as possible to the AAA spec, but it may not always be possible.

 We have a lot of colors that will not reach spec, which is why we have debug console warnings disabled by default.
*/


// we move in .1 increments and need to match the contrast ratio of 7:1
// so the max iterations we'll ever need before know we cannot get a valid spec is 7 / .1 = 70
const MAX_ITERATIONS = 71;

// quick set/remove for local dev (really just to avoid needing to type it in console every time I want to toggle it on/off)
// localStorage.setItem('debug-color-contrasts', 'true');
// localStorage.removeItem('debug-color-contrasts');

const debug = localStorage.getItem('debug-color-contrasts');
const contrastColorMemo = new Map<string, string>();
let gcTimeout: NodeJS.Timeout;

const gcColorMemo = () => {
	clearTimeout(gcTimeout);
	gcTimeout = setTimeout(() => {
		if (contrastColorMemo.size > 5000) { // if user goes crazy with color changes we'll dump the memo to avoid memory leaks
			contrastColorMemo.clear();
		}
	}, 500);
};

const addColorToMemo = (colorKey: string, contrastColor: string) => {
	contrastColorMemo.set(colorKey, contrastColor);
	gcColorMemo();
};

type TOptions = {
	maxIterations?: number;
	/** If AAA (7:1) is unreachable against the target, accept anything ≥ AA (4.5:1) instead. */
	fallbackToAA?: boolean;
	/**
	 * If the active threshold (AAA, or AA when `fallbackToAA` is on) is unreachable, return whichever
	 * darkened/lightened endpoint produced the highest contrast — even if it's still below threshold.
	 * Independent of `fallbackToAA`: combine to get a full AAA → AA → best-possible cascade.
	 * When false (default), an unmet threshold returns the original `colorToChange` unchanged.
	 */
	fallbackToBestPossible?: boolean;
	/** Chase AA (4.5:1) instead of AAA (7:1). */
	targetAA?: boolean;
}

const getRelativeContrastColor = (colorToChange: string, colorToContrastAgainst: string, options?: TOptions) => {
	const {
		fallbackToAA = false,
		fallbackToBestPossible = false,
		maxIterations = MAX_ITERATIONS,
		targetAA = false,
	} = options || {};
	const memoKey = `${colorToChange}|${colorToContrastAgainst}|${fallbackToAA ? 'aa' : 'aaa'}|${fallbackToBestPossible ? 'best' : 'strict'}|${targetAA ? 'target-aa' : 'target-aaa'}`;
	const memoized = contrastColorMemo.get(memoKey);
	if (memoized) {
		return memoized;
	}

	if (!chroma.valid(colorToChange) || !chroma.valid(colorToContrastAgainst)) {
		console.warn('getRelativeContrastColor: invalid color');
		addColorToMemo(memoKey, colorToChange);
		return colorToChange;
	}

	const targetRatio = targetAA ? AA_CONTRAST_RATIO : AAA_COTNRAST_RATIO;

	if (chroma.contrast(colorToChange, colorToContrastAgainst) >= targetRatio) {
		addColorToMemo(memoKey, colorToChange);
		return colorToChange;
	}

	// Pick the threshold we'll actually chase. WCAG contrast is luminance-only, so the maximum
	// achievable ratio against any background is bounded by max(contrast(bg, black), contrast(bg, white)).
	// For mid-luminance backgrounds (e.g. #908ca0 ≈ 6.44:1 max) AAA is unreachable and the loop would
	// converge to black/white pointlessly. When fallbackToAA is on, drop to AA in that case.
	let threshold = targetRatio;
	if (fallbackToAA) {
		const maxAchievable = Math.max(
			chroma.contrast('#000000', colorToContrastAgainst),
			chroma.contrast('#ffffff', colorToContrastAgainst),
		);
		if (maxAchievable < AAA_COTNRAST_RATIO) {
			threshold = AA_CONTRAST_RATIO;
		}
	}

	let darkenedColor = colorToChange;
	let lightenedColor = colorToChange;
	let iterations = 0;

	while (
		chroma.contrast(darkenedColor, colorToContrastAgainst) < threshold
		&& chroma.contrast(lightenedColor, colorToContrastAgainst) < threshold
		&& iterations < maxIterations
	) {

		// alter the color by darkening, but don't unnecessarily darken if we already have a valid contrast
		darkenedColor = chroma.contrast(darkenedColor, colorToContrastAgainst) < threshold
			? chroma(darkenedColor).darken(.1).hex()
			: darkenedColor;

		// alter the color by lightening, but don't unnecessarily lighten if we already have a valid contrast
		lightenedColor = chroma.contrast(lightenedColor, colorToContrastAgainst) < threshold
			? chroma(lightenedColor).brighten(.1).hex()
			: lightenedColor;

		iterations++;
	}

	if (debug && iterations >= maxIterations) {
		console.warn('getRelativeContrastColor: too many iterations:');
		console.log('colorToChange', colorToChange);
		console.log('colorToContrastAgainst', colorToContrastAgainst);
		console.log('threshold', threshold);
		console.log('darkenedColor', darkenedColor, 'ratio:', chroma.contrast(darkenedColor, colorToContrastAgainst).toFixed(2));
		console.log('lightenedColor', lightenedColor, 'ratio:', chroma.contrast(lightenedColor, colorToContrastAgainst).toFixed(2));
	}

	const contrastA = Number(chroma.contrast(darkenedColor, colorToContrastAgainst).toFixed(2));
	const contrastB = Number(chroma.contrast(lightenedColor, colorToContrastAgainst).toFixed(2));
	const bestColor = contrastA >= contrastB ? darkenedColor : lightenedColor;
	const bestContrast = Math.max(contrastA, contrastB);

	// Threshold met (with whichever tier we ended up chasing) — return the best endpoint.
	if (bestContrast >= threshold) {
		addColorToMemo(memoKey, bestColor);
		return bestColor;
	}

	// Threshold not met. Only return the best endpoint if the caller opted in; otherwise the
	// original color is returned so callers can decide how to handle the "no valid contrast" case.
	if (fallbackToBestPossible) {
		addColorToMemo(memoKey, bestColor);
		return bestColor;
	}

	addColorToMemo(memoKey, colorToChange);
	return colorToChange;
};

export default getRelativeContrastColor;
